import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { getLogo } from "src/shared/services/services";
import PrintAppFind from "src/print-configuration/PrintAppFind";
import { fetchPrintConfig } from "../configurations/commonPrint/api/printConfig";
import { defaultConfig, PrintLayout } from "../configurations/commonPrint/hooks/usePrintConfig";
import { Configration, defaultUserContextProps, SubscriptionInfo, toBoolean, UserContextProps, UserDetails } from "./contextInterface";
import { getCreditControlSettings } from "../api";
import { BlockingRulesData, CreditLimitData, ReminderData, WhatsAppData } from "../configurations/creditControl/types";
import { socket } from "src/features/auth/socket";


// ── NEW: helper to read the session token ─────────────────────────────────────
const getSessionToken = (): string | null =>
  sessionStorage.getItem("jsonwebtoken"); // 🔑 change key to match yours

const UserContext = createContext<UserContextProps | undefined>(undefined);

// ─────────────────────────────────────────────────────────────────────────────
export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserDetails | null>(null);

  // NEW: refresh counter — incrementing it re-triggers all dependent useEffects
  const [refreshKey, setRefreshKey] = useState(0);
  const refreshUserContext = useCallback(() => setRefreshKey((k) => k + 1), []);


  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);
  const [userConfigration, setUserConfigration] = useState<Configration>(defaultUserContextProps);
  const [printConfig, setPrintConfig] = useState<PrintLayout>();

  const [creditLimit, setCreditLimit] = useState<CreditLimitData>({
    defaultLimit: 1000,
    maxLimit: 5000,
    allowNewCustomers: true,
    lowBalanceAlert: 80,
  });

  const [blockingRules, setBlockingRules] = useState<BlockingRulesData>({
    blockOnExceed: true,
    allowDraftOrders: true,
    requireApproval: true,
  });

  const [whatsapp, setWhatsapp] = useState<WhatsAppData>({
    enabled: true,
    number: "",
    language: "English",
    sendOrderReady: true,
    sendPaymentReminder: true,
  });

  const [reminder, setReminder] = useState<ReminderData>({
    enabled: true,
    days: "1, 3, 7, 15",
    time: "11:00 AM",
  });

  const usePrinterList = () => {
    if (Boolean(userConfigration.appPrinterEnabled) === true) {

      const { newPrinterList } = PrintAppFind(); // always called unconditionally
      const hasToken = Boolean(getSessionToken());
      return hasToken ? newPrinterList : [];     // filter result, not the call
    }
  };

  // Token-gated printer list (no token → [])
  const NewPrinterList = usePrinterList() ?? "";

  // ── setConfigurations (unchanged logic, same as before) ───────────────────
  const setConfigurations = (config: Configration) => {
    if (config.contactQrCode) {
      const base64 = config.contactQrCode.startsWith("data:image")
        ? config.contactQrCode
        : `data:image/png;base64,${config.contactQrCode}`;
      config = { ...config, contactQrCode: base64 };
    }
    if (config.locationQrCode) {
      const base64 = config.locationQrCode.startsWith("data:image")
        ? config.locationQrCode
        : `data:image/png;base64,${config.locationQrCode}`;
      config = { ...config, locationQrCode: base64 };
    }
    if (config) {
      setUserConfigration((prev) => ({ ...prev, ...config }));
    }
  };

  // ── businessType → orderTableType / printType ─────────────────────────────
  // refreshKey added so a manual refresh also re-evaluates this
  useEffect(() => {
    const businessType = user?.businessType || "";
    const isGrocery = businessType.toLowerCase() === "grocery";
    setUserConfigration((prev) => ({
      ...prev,
      orderTableType: isGrocery ? "Table Type:2" : prev.orderTableType,
    }));
  }, [user?.businessType, refreshKey]);


  // ── Printer / QR code AWS URL resolution ──────────────────────────────────
  useEffect(() => {
    const cleanBase64 = (value: string | undefined) => {
      if (!value) return "";
      return value.replace(/^data:image\/[a-zA-Z]+;base64,/, "");
    };
    const imageUrl = cleanBase64(user?.businessLogo);
    const qrCodeUrl = cleanBase64(userConfigration?.contactQrCode);
    const locationQrCodeUrl = cleanBase64(userConfigration?.locationQrCode);

    if (
      NewPrinterList.length > 0 &&
      toBoolean(userConfigration?.appPrinterEnabled) === true
    ) {
      setUser((prev) =>
        prev
          ? { ...prev, companyLogoAPP: `${process.env.REACT_APP_IMAGE_PATH}/${imageUrl}` }
          : prev
      );
      setUserConfigration((prev) => ({
        ...prev,
        contact_qr_codeAWS: `${process.env.REACT_APP_IMAGE_PATH}/${qrCodeUrl}`,
        location_qr_codeAWS: `${process.env.REACT_APP_IMAGE_PATH}/${locationQrCodeUrl}`,
        appPrinter: true,
      }));
    } else if (toBoolean(userConfigration?.appPrinterEnabled) === false) {
      setUser((prev) => (prev ? { ...prev, companyLogoAPP: user?.companyLogo } : prev));

      getLogo(qrCodeUrl)
        .then((response: any) => {
          const blob = new Blob([response.data], { type: "image/png" });
          const imageUrl = URL.createObjectURL(blob);
          setUserConfigration((prev) => ({
            ...prev,
            contact_qr_codeAWS: imageUrl,
            appPrinter: false,
          }));
        })
        .catch((err) => console.error(err));

      getLogo(locationQrCodeUrl)
        .then((response: any) => {
          const blob = new Blob([response.data], { type: "image/png" });
          const imageUrl = URL.createObjectURL(blob);
          setUserConfigration((prev) => ({
            ...prev,
            location_qr_codeAWS: imageUrl,
            appPrinter: false,
          }));
        })
        .catch((err) => console.error(err));
    }
  }, [
    NewPrinterList,
    userConfigration?.contactQrCode,
    userConfigration?.locationQrCode,
    user?.companyLogo,
    userConfigration?.appPrinterEnabled,
    user?.businessLogo,
    refreshKey, // re-run on manual refresh
  ]);

  // creditControlEnabled is now part of userConfigration, so we can handle it here

  useEffect(() => {
    if (userConfigration && userConfigration.creditControlEnabled && userConfigration?.creditControlEnabled === true) {

      getCreditControlSettings()
        .then((data) => {
          setCreditLimit(data.creditLimit);
          setBlockingRules(data.blockingRules);
          setWhatsapp(data.whatsapp);
          setReminder(data.reminder);
        })
        .catch(() => {
          // keep default values if fetch fails (e.g. first-time setup)
        });
    }
  }, [userConfigration?.creditControlEnabled, refreshKey]);


  // ── Custom print layout fetch ──────────────────────────────────────────────
  useEffect(() => {
    if (user?.orgName && userConfigration?.customEnabledPrint) {
      fetchPrintConfig()
        .then((res) => {
          const getData: PrintLayout[] = res.data;
          const customLayout = [defaultConfig, ...getData];
          const isWorkingLayout = customLayout.find(
            (layout) => layout._id.$oid === userConfigration.printLayoutId
          );
          setPrintConfig(isWorkingLayout);
        })
        .catch((error) => console.error("Failed to fetch print config:", error));
    }
  }, [user?.orgName, userConfigration?.customEnabledPrint, userConfigration.printLayoutId, refreshKey]);



  // ─────────────────────────────────────────────────────────────────────────
  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        userConfigration,
        setConfigurations,
        subscriptionInfo,
        setSubscriptionInfo,
        printConfig,
        refreshUserContext, // NEW — expose to consumers
        creditLimit,
        blockingRules,
        whatsapp,
        reminder,
        setReminder,
        setWhatsapp,
        setCreditLimit,
        setBlockingRules,

      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) throw new Error("useUser must be used within a UserProvider");
  return context;
};